import * as React from 'react'

import { inject, MobXProviderContext, observer } from 'mobx-react'

import MobileResourceEditDrawer from '~/client/src/mobile/components/MobileResource/MobileResourceEditDrawer'
import { useChosenTemplateGate } from '~/client/src/mobile/components/MobileResource/useChosenTemplateGate'
import { useMobileResourceEditRecord } from '~/client/src/mobile/components/MobileResource/useMobileResourceEditRecord'
import { useResetOnOpen } from '~/client/src/mobile/components/MobileResource/useResetOnOpen'
import { EquipmentResourceEditBody } from '~/client/src/shared/components/EquipmentResourceEdit/EquipmentResourceEditBody'
import { FormListSelector } from '~/client/src/shared/components/FormListSelector'
import { FormPropertiesSidebar } from '~/client/src/shared/components/FormPropertiesSidebar'
import { ResourceEdit } from '~/client/src/shared/components/ResourceViewEdit'
import useLocationResources from '~/client/src/shared/hooks/useLocationResources'
import { extractControlFields } from '~/client/src/shared/hooks/useResourceControls'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import UserProject from '~/client/src/shared/models/UserProject'
import InitialState from '~/client/src/shared/stores/InitialState'

import {
  useMobileResourceActions,
  useMobileResourceMeta,
} from '../../components/MobileResource/context'

interface IProps {
  state?: InitialState
}

/**
 * Locations reuse EquipmentResourceEditBody, which is resource-agnostic apart from its
 * name: no linkField is passed, so the StruxTag link row and its telemetry sections do
 * not render. The same body backs the desktop map card.
 */
const MobileLocationsEditPanel: React.FC<IProps> = ({ state }) => {
  const { closeEdit } = useMobileResourceActions()
  const { resourceTypeId, runtimeContext } = useMobileResourceMeta()
  const {
    isOpen,
    activeRecordId,
    selectedRecord,
    editableRecord,
    clearActiveRecord,
  } = useMobileResourceEditRecord()

  const projectId = state?.activeProject?.id
  const { save, schema, controlCommands } = useLocationResources({ projectId })

  const stores = React.useContext(MobXProviderContext) as any
  const isAdmin = UserProject.isAdmin(
    stores.state?.user,
    stores.userProjectsStore,
  )

  const [isCreatingNewForm, setIsCreatingNewForm] = React.useState(false)
  const isCreationMode = activeRecordId === 'new'

  const { setChosenTemplateId, effectiveTemplateId, templates } =
    useChosenTemplateGate({
      isCreationMode,
      templates: schema.templates,
      loading: schema.loading,
    })

  useResetOnOpen(isOpen, () => {
    setIsCreatingNewForm(false)
    setChosenTemplateId(null)
  })

  const qrCodeUrl = React.useMemo(() => {
    if (!activeRecordId || isCreationMode) return null
    return `${window.location.origin}/resource/${resourceTypeId}/${activeRecordId}`
  }, [activeRecordId, isCreationMode, resourceTypeId])

  const handleSave = React.useCallback(
    async (formData: Record<string, any>) => {
      await save(activeRecordId, formData)
    },
    [save, activeRecordId],
  )

  if (!activeRecordId) return null

  if (isCreationMode && !effectiveTemplateId) {
    return (
      <MobileResourceEditDrawer
        open={isOpen}
        onClose={closeEdit}
        onCloseComplete={clearActiveRecord}
      >
        {isCreatingNewForm ? (
          <FormPropertiesSidebar
            mode="create"
            resourceTypeId={resourceTypeId}
            onClose={closeEdit}
            onBackToPicker={() => setIsCreatingNewForm(false)}
            resourceSchema={schema}
            onCreated={newTemplateId => {
              setIsCreatingNewForm(false)
              setChosenTemplateId(newTemplateId)
            }}
          />
        ) : (
          <FormListSelector
            title={Localization.translator.selectLocationForm}
            titleVariant="panel"
            resourceTypeId={resourceTypeId}
            onSelect={setChosenTemplateId}
            onClose={closeEdit}
            onCreateNewForm={
              isAdmin ? () => setIsCreatingNewForm(true) : undefined
            }
            templates={schema.templates}
            loading={schema.loading}
          />
        )}
      </MobileResourceEditDrawer>
    )
  }

  return (
    <MobileResourceEditDrawer
      open={isOpen}
      onClose={closeEdit}
      onCloseComplete={clearActiveRecord}
    >
      <EquipmentResourceEditBody
        recordId={activeRecordId}
        record={editableRecord}
        templateId={
          isCreationMode ? effectiveTemplateId || undefined : undefined
        }
        resourceTypeId={resourceTypeId}
        runtimeContext={runtimeContext}
        projectId={projectId}
        qrCodeUrl={qrCodeUrl}
        isCreationMode={isCreationMode}
        controlFields={extractControlFields(selectedRecord?.root ?? {})}
        controlCommands={controlCommands}
        onClose={closeEdit}
        onSave={handleSave}
        onTemplateChange={setChosenTemplateId}
        resourceSchema={schema}
        formSelector={
          <ResourceEdit.FormSelector
            resourceTypeId={resourceTypeId}
            templates={templates}
            currentTemplateId={effectiveTemplateId}
          />
        }
      />
    </MobileResourceEditDrawer>
  )
}

export default inject('state')(observer(MobileLocationsEditPanel))
